import { useEffect, useState, createContext, useContext, type ReactNode } from 'react'
import { Routes, Route, NavLink, Navigate, useNavigate } from 'react-router-dom'
import { api } from './api'
import { Spinner } from './components/ui'

import Login from './pages/Login'
import Setup from './pages/Setup'
import Dashboard from './pages/Dashboard'
import Clients from './pages/Clients'
import ClientDetail from './pages/ClientDetail'
import Items from './pages/Items'
import Invoices from './pages/Invoices'
import InvoiceEdit from './pages/InvoiceEdit'
import InvoiceView from './pages/InvoiceView'
import Payments from './pages/Payments'
import Expenses from './pages/Expenses'
import Recurring from './pages/Recurring'
import Reports from './pages/Reports'
import Settings from './pages/Settings'

export interface User {
  id: string
  email: string
  name: string
  role: 'owner' | 'accountant' | 'viewer'
}

interface AuthState {
  user: User | null
  needsSetup: boolean
  appName: string
  secretConfigured: boolean
  refresh: () => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthState>(null as unknown as AuthState)
export const useAuth = () => useContext(AuthContext)

export default function App() {
  const [state, setState] = useState<{
    user: User | null
    needsSetup: boolean
    appName: string
    secretConfigured: boolean
  } | null>(null)

  const refresh = async () => {
    const s = await api.get<{
      user: User | null
      needsSetup: boolean
      appName: string
      secretConfigured: boolean
    }>('/auth/state')
    setState(s)
  }

  useEffect(() => {
    refresh().catch(() => setState({ user: null, needsSetup: false, appName: 'DigiAud Invoicing', secretConfigured: false }))
  }, [])

  if (!state) return <Spinner label="Starting…" />

  const auth: AuthState = {
    ...state,
    refresh,
    logout: async () => {
      await api.post('/auth/logout')
      setState({ ...state, user: null })
    },
  }

  return (
    <AuthContext.Provider value={auth}>
      {state.needsSetup ? <Setup /> : !state.user ? <Login /> : <Shell />}
    </AuthContext.Provider>
  )
}

function Shell() {
  return (
    <div className="app">
      <Sidebar />
      <div className="main">
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/clients" element={<Clients />} />
          <Route path="/clients/:id" element={<ClientDetail />} />
          <Route path="/items" element={<Items />} />
          <Route path="/invoices" element={<Invoices />} />
          <Route path="/invoices/new" element={<InvoiceEdit />} />
          <Route path="/invoices/:id/edit" element={<InvoiceEdit />} />
          <Route path="/invoices/:id" element={<InvoiceView />} />
          <Route path="/payments" element={<Payments />} />
          <Route path="/expenses" element={<Expenses />} />
          <Route path="/recurring" element={<Recurring />} />
          <Route path="/reports" element={<Reports />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </div>
  )
}

function Sidebar() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()

  return (
    <aside className="sidebar">
      <div className="brand">
        <div className="name">DIGIAUD (OPC)</div>
        <div className="sub">PRIVATE LIMITED · Invoicing</div>
      </div>
      <nav className="nav">
        <NavItem to="/" icon="◧" label="Dashboard" end />
        <div className="group">Billing</div>
        <NavItem to="/invoices" icon="▤" label="Invoices" />
        <NavItem to="/payments" icon="₹" label="Payments" />
        <NavItem to="/expenses" icon="◇" label="Expenses" />
        <NavItem to="/recurring" icon="↻" label="Recurring" />
        <div className="group">Records</div>
        <NavItem to="/clients" icon="◍" label="Clients" />
        <NavItem to="/items" icon="≡" label="Items & services" />
        <div className="group">Insight</div>
        <NavItem to="/reports" icon="◪" label="Reports & GST" />
        <NavItem to="/settings" icon="⚙" label="Settings" />
      </nav>
      <div className="foot">
        <div style={{ fontWeight: 600, color: 'var(--ink)' }}>{user?.name}</div>
        <div style={{ marginBottom: 8 }}>{user?.email}</div>
        <button
          className="btn sm"
          onClick={async () => {
            await logout()
            navigate('/')
          }}
        >
          Sign out
        </button>
      </div>
    </aside>
  )
}

function NavItem({
  to,
  icon,
  label,
  end,
}: {
  to: string
  icon: string
  label: string
  end?: boolean
}) {
  return (
    <NavLink to={to} end={end} className={({ isActive }) => (isActive ? 'active' : '')}>
      <span className="ico">{icon}</span>
      {label}
    </NavLink>
  )
}

export function Page({
  title,
  subtitle,
  actions,
  children,
  narrow,
}: {
  title: ReactNode
  subtitle?: ReactNode
  actions?: ReactNode
  children: ReactNode
  narrow?: boolean
}) {
  return (
    <>
      <div className="topbar">
        <div>
          <h1>{title}</h1>
          {subtitle && <div className="sub">{subtitle}</div>}
        </div>
        {actions && <div className="btn-row">{actions}</div>}
      </div>
      <div className="content">
        <div className={narrow ? 'container narrow' : 'container'}>{children}</div>
      </div>
    </>
  )
}
